import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { catalogGroupsFromPayload, formsForParent, isChildFormType, PARENT_PATH_BY_TYPE } from '../utils/formTypeIds';
import { inferCancellingSide, normalizeRepresentedSides } from '../utils/childFormWizardFields';
import { wizardSpecForFormType } from '../utils/formTypeRegistry';
import { fetchFormCatalog } from '../utils/childFormApi';
import CreateFormModal from './CreateFormModal';
import useViewerSubscription from '../hooks/useViewerSubscription';

export function dealChildFormViewPath(result, parentType, parentId) {
  const splitId = result?.split_attachment_id;
  if (!splitId || !parentId) return null;
  const path = `/${PARENT_PATH_BY_TYPE[parentType] || 'contract'}/${parentId}`;
  return `${path}?openDoc=${splitId}`;
}

const GenerateFormControl = (props) => (
  <GenerateFormControlInner key={`${props.parentType}:${props.parentId}`} {...props} />
);

const GenerateFormControlInner = ({
  parentType,
  parentId,
  userEmail,
  disabled = false,
  initialFormType = '',
  representedSides,
  onAutoStarted,
  onWizardClosed,
}) => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const {
    loaded: accessLoaded,
    profile,
    accessReady,
    canGenerateForms,
    formGenerationPlanBlocked,
    currentPlanName,
    upgradePlanName,
  } = useViewerSubscription();
  const viewerKnown = Boolean(accessLoaded && (accessReady || profile));
  const showPlanUpgrade = Boolean(
    viewerKnown && formGenerationPlanBlocked && !canGenerateForms,
  );
  const accessDenied = Boolean(viewerKnown && !canGenerateForms && !showPlanUpgrade);
  const [open, setOpen] = useState(false);
  const [formType, setFormType] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(false);
  const [catalogReload, setCatalogReload] = useState(0);
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (showPlanUpgrade || accessDenied) {
      setCatalog(null);
      setCatalogError(false);
      return undefined;
    }
    let cancelled = false;
    setCatalogError(false);
    fetchFormCatalog({ userEmail, parentType, parentId, getAccessTokenSilently, getIdTokenClaims }).then((payload) => {
      const groups = catalogGroupsFromPayload(payload);
      if (!cancelled) setCatalog(groups);
    }).catch(() => {
      if (!cancelled) {
        setCatalog(null);
        setCatalogError(true);
      }
    });
    return () => { cancelled = true; };
  }, [userEmail, parentType, parentId, getAccessTokenSilently, getIdTokenClaims, catalogReload, showPlanUpgrade, accessDenied]);
  const catalogReady = catalog !== null;
  const allowed = useMemo(
    () => formsForParent(parentType, catalog || []),
    [parentType, catalog],
  );
  const selected = selectedCard || allowed.find((form) => form.formType === formType);
  const catalogForSpec = selected
    ? [{ group: 'picked', forms: [selected] }, ...(catalog || [])]
    : (catalog || []);
  const spec = wizardSpecForFormType(formType, {
    catalog: catalogForSpec,
    createsEntity: selected?.createsEntity,
  });
  const closeWizard = (result) => {
    setFormType(null);
    setSelectedCard(null);
    if (onWizardClosed) {
      onWizardClosed(result);
    }
    const path = dealChildFormViewPath(result, parentType, parentId);
    if (path) navigate(path);
  };
  const backToCatalog = () => {
    setFormType(null);
    setSelectedCard(null);
    setOpen(true);
  };

  useEffect(() => {
    const wanted = String(initialFormType || '').trim();
    if (!wanted || autoStartedRef.current || disabled || accessDenied) {
      return undefined;
    }
    if (!catalogReady && !catalogError) {
      return undefined;
    }
    const card = allowed.find((form) => form.formType === wanted) || {
      formType: wanted,
      createsEntity: false,
    };
    autoStartedRef.current = true;
    setFormType(card.formType);
    setSelectedCard(card);
    setOpen(false);
    if (onAutoStarted) {
      onAutoStarted(card.formType);
    }
    return undefined;
  }, [
    accessDenied,
    allowed,
    catalogError,
    catalogReady,
    disabled,
    initialFormType,
    onAutoStarted,
  ]);

  const parentValues = useMemo(() => {
    const represented_sides = normalizeRepresentedSides(representedSides);
    const values = {
      parent_contract_id: parentType === 'contract' ? parentId : '',
      parent_listing_id: parentType === 'listing' ? parentId : '',
      parent_buyer_agreement_id: parentType === 'buyer_agreement' ? parentId : '',
      represented_sides,
    };
    const inferred = inferCancellingSide(values);
    if (inferred) values.cancelling_side = inferred;
    return values;
  }, [parentType, parentId, representedSides]);

  if (disabled || accessDenied || (catalogReady && !showPlanUpgrade && !allowed.length)) return null;

  return (
    <>
      <button
        className="btn-primary"
        type="button"
        onClick={() => {
          if (catalogError) setCatalogReload((n) => n + 1);
          setOpen(true);
        }}
      >
        {catalogError ? 'Retry loading forms' : '+ Create Form'}
      </button>
      <CreateFormModal
        isOpen={open && !formType && (showPlanUpgrade || catalogReady)}
        onClose={() => setOpen(false)}
        onSelectForm={(nextType, card) => {
          setFormType(nextType);
          setSelectedCard(card || null);
          setOpen(false);
        }}
        parentType={parentType}
        parentId={parentId}
        userEmail={userEmail}
        getAccessTokenSilently={getAccessTokenSilently}
        getIdTokenClaims={getIdTokenClaims}
        catalog={catalog}
        upgradeRequired={showPlanUpgrade}
        currentPlanName={currentPlanName}
        upgradePlanName={upgradePlanName}
      />
      {spec && (isChildFormType(formType, catalogForSpec) || formType === 'purchase_agreement') && spec.buildDealProps && (
        <spec.Wizard
          {...spec.buildDealProps({
            userEmail,
            parentType,
            parentId,
            initialValues: parentValues,
            wizardSteps: selected?.wizardSteps || [],
            optionalWizardFields: selected?.optionalWizardFields || [],
            stillNeededLabels: selected?.stillNeededLabels || {},
            allowEarlySubmitAfter: selected?.allowEarlySubmitAfter || '',
            onClose: closeWizard,
            onBackToCatalog: backToCatalog,
          })}
        />
      )}
    </>
  );
};

export default GenerateFormControl;
